import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { ConfigService } from '../../../config.service';
import { LoggingService } from '../../../logging.service';
import { TranslationBundle, TranslationService } from '../../../translation.service';

export interface DeleteReasonOption {
  key: string
  value: string
}

@Component({
  host: {
    'class': 'fw-delete-reason',
    '[class.compact]': 'isCompact',
    'test-id': 'fw-delete-reason',
  },
  selector: 'fw-delete-reason',
  styleUrls: ['./fw-delete-reason.component.scss'],
  templateUrl: './fw-delete-reason.component.html',
})
export class FwDeleteReasonComponent implements OnDestroy {
  public auditTrailEnabled = false;
  public deleteReasons: DeleteReasonOption[] = [];
  public form = new FormGroup({
    deleteReason: new FormControl('', [Validators.required, Validators.minLength(2)]),
  });
  public get invalid(): boolean {
    return this.form.invalid;
  }
  public get valid(): boolean {
    return this.form.valid;
  }
  public get value(): string {
    return this.form.value.deleteReason;
  }

  @Input() layout: 'compact' | 'default' = 'default'
  public get isCompact(): boolean {
    return this.layout === 'compact';
  }

  @Output() change = new EventEmitter<string>()

  private messages = TranslationBundle.EMPTY
  private subscriptions = { messages: Subscription.EMPTY }

  constructor(
    public configService: ConfigService,
    public logger: LoggingService,
    private translate: TranslationService,
  ) {
    this.subscriptions.messages = this.translate.getBundle('fwDeleteReason').subscribe(messages => {
      this.messages = messages;
    });
    this.auditTrailEnabled = this.configService.isFeatureEnabled('audit_trail');
    if (this.auditTrailEnabled) {
      this.configService.getDeleteReasons().pipe(
        first(),
        map((data) => Object.entries(data).map(([key, value]) => ({ key, value })))
      ).subscribe(result => {
        this.deleteReasons = result;
      });
    } else {
      this.logger.warn(this.messages.get('auditTrailWarning'));
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  onChangeReason(): void {
    this.change.emit(this.form.value.deleteReason);
  }

  setValue(value: string): void {
    if (!this.deleteReasons.map((v) => v.key).includes(value)) {
      this.logger.error(this.translate.interpolate(this.messages.get('invalidInputError'), value));
      return;
    }
    this.form.setValue({ deleteReason: value });
    this.onChangeReason();
  }
}
